/**
 * @fileoverview Smart DOM Update System
 * Intelligent DOM updates that preserve state and minimize reflow
 * Inspired by Horizon's morphing system but simplified for our needs
 */

import { Debug, cartPerformance } from '../utils/index.js';

/**
 * Smart DOM updater that minimizes reflow and preserves element state
 */
export class SmartUpdater {
  constructor() {
    this.updateQueue = new Map();
    this.isProcessing = false;
    this.observers = new Map();
    this.preserveAttributes = ['data-loading', 'data-error', 'aria-expanded'];
  }

  /**
   * Updates an element's content intelligently
   * @param {HTMLElement} element - Element to update
   * @param {string|HTMLElement} newContent - New content
   * @param {Object} options - Update options
   * @param {boolean} options.preserveState - Whether to preserve element state
   * @param {boolean} options.animate - Whether to animate the update
   * @param {string[]} options.preserveAttributes - Attributes to preserve
   * @returns {Promise<void>}
   */
  async updateElement(element, newContent, options = {}) {
    const {
      preserveState = true,
      animate = true,
      preserveAttributes = this.preserveAttributes
    } = options;

    const performanceMarker = cartPerformance.createStartingMarker('smart-update:element');

    try {
      // Parse new content if it's a string
      const newElement = typeof newContent === 'string' 
        ? this.parseContent(newContent, element.tagName)
        : newContent;

      if (!newElement) {
        Debug.log('warn', 'SmartUpdater: Invalid new content provided');
        return;
      }

      // Check if update is actually needed
      if (this.isContentSame(element, newElement)) {
        Debug.log('debug', 'SmartUpdater: Content unchanged, skipping update');
        return;
      }

      // Preserve state before update
      const preservedState = preserveState ? this.preserveElementState(element, preserveAttributes) : null;

      // Perform the update
      if (animate && this.supportsAnimations()) {
        await this.animatedUpdate(element, newElement, preservedState);
      } else {
        await this.immediateUpdate(element, newElement, preservedState);
      }

      // Trigger any observers
      this.notifyObservers(element, 'updated');

    } catch (error) {
      Debug.log('error', 'SmartUpdater: Update failed', error);
      throw error;
    } finally {
      if (performanceMarker) {
        cartPerformance.measureFromMarker(performanceMarker);
      }
    }
  }

  /**
   * Updates multiple elements in batch
   * @param {Array<{element: HTMLElement, content: string|HTMLElement, options?: Object}>} updates - Updates to perform
   * @returns {Promise<void>}
   */
  async batchUpdate(updates) {
    const performanceMarker = cartPerformance.createStartingMarker('smart-update:batch');

    try {
      // Group updates by parent to minimize reflow
      const groupedUpdates = this.groupUpdatesByParent(updates);

      // Process each group
      for (const [parent, elementUpdates] of groupedUpdates) {
        await this.updateElementGroup(parent, elementUpdates);
      }

    } catch (error) {
      Debug.log('error', 'SmartUpdater: Batch update failed', error);
      throw error;
    } finally {
      if (performanceMarker) {
        cartPerformance.measureFromMarker(performanceMarker);
      }
    }
  }

  /**
   * Parses string content into DOM element
   * @param {string} content - HTML content
   * @param {string} tagName - Expected tag name
   * @returns {HTMLElement|null}
   */
  parseContent(content, tagName) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');
      
      // Find the element with matching tag name
      const element = doc.querySelector(tagName.toLowerCase()) || doc.body.firstElementChild;
      
      return element;
    } catch (error) {
      Debug.log('error', 'SmartUpdater: Content parsing failed', error);
      return null;
    }
  }

  /**
   * Checks if content is the same between elements
   * @param {HTMLElement} element1 - First element
   * @param {HTMLElement} element2 - Second element
   * @returns {boolean}
   */
  isContentSame(element1, element2) {
    // Quick check: compare innerHTML
    if (element1.innerHTML === element2.innerHTML) {
      return true;
    }

    // Deep check: compare text content and key attributes
    const content1 = this.getElementSignature(element1);
    const content2 = this.getElementSignature(element2);
    
    return content1 === content2;
  }

  /**
   * Gets a signature for an element for comparison
   * @param {HTMLElement} element - Element to get signature for
   * @returns {string}
   */
  getElementSignature(element) {
    const text = element.textContent?.trim() || '';
    const key = element.dataset.key || element.id || '';
    const classes = element.className || '';
    
    return `${text}|${key}|${classes}`;
  }

  /**
   * Preserves element state before update
   * @param {HTMLElement} element - Element to preserve state for
   * @param {string[]} attributes - Attributes to preserve
   * @returns {Object}
   */
  preserveElementState(element, attributes) {
    const state = {
      attributes: {},
      properties: {},
      focus: document.activeElement === element,
      scroll: {
        top: element.scrollTop,
        left: element.scrollLeft
      }
    };

    // Preserve specified attributes
    attributes.forEach(attr => {
      if (element.hasAttribute(attr)) {
        state.attributes[attr] = element.getAttribute(attr);
      }
    });

    // Preserve input values
    if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
      state.properties.value = element.value;
      state.properties.checked = element.checked;
    }

    // Preserve custom properties
    if (element.dataset.preserveState) {
      state.properties.preserveState = element.dataset.preserveState;
    }

    return state;
  }

  /**
   * Restores element state after update
   * @param {HTMLElement} element - Element to restore state for
   * @param {Object} state - State to restore
   */
  restoreElementState(element, state) {
    if (!state) return;

    // Restore attributes
    Object.entries(state.attributes).forEach(([attr, value]) => {
      element.setAttribute(attr, value);
    });

    // Restore properties
    Object.entries(state.properties).forEach(([prop, value]) => {
      if (prop in element) {
        element[prop] = value;
      }
    });

    // Restore focus
    if (state.focus) {
      element.focus();
    }

    // Restore scroll position
    if (state.scroll) {
      element.scrollTop = state.scroll.top;
      element.scrollLeft = state.scroll.left;
    }
  }

  /**
   * Performs animated update
   * @param {HTMLElement} element - Element to update
   * @param {HTMLElement} newElement - New element content
   * @param {Object} preservedState - Preserved state
   * @returns {Promise<void>}
   */
  async animatedUpdate(element, newElement, preservedState) {
    // Add update animation class
    element.classList.add('cart-updating');

    // Use requestAnimationFrame for smooth updates
    return new Promise(resolve => {
      requestAnimationFrame(() => {
        // Update content
        element.innerHTML = newElement.innerHTML;
        
        // Copy attributes
        this.copyAttributes(newElement, element);
        
        // Restore state
        this.restoreElementState(element, preservedState);
        
        // Remove animation class after a short delay
        setTimeout(() => {
          element.classList.remove('cart-updating');
          resolve();
        }, 150);
      });
    });
  }

  /**
   * Performs immediate update
   * @param {HTMLElement} element - Element to update
   * @param {HTMLElement} newElement - New element content
   * @param {Object} preservedState - Preserved state
   * @returns {Promise<void>}
   */
  async immediateUpdate(element, newElement, preservedState) {
    // Update content
    element.innerHTML = newElement.innerHTML;
    
    // Copy attributes
    this.copyAttributes(newElement, element);
    
    // Restore state
    this.restoreElementState(element, preservedState);
  }

  /**
   * Copies relevant attributes from source to target
   * @param {HTMLElement} source - Source element
   * @param {HTMLElement} target - Target element
   */
  copyAttributes(source, target) {
    const attributesToCopy = ['data-key', 'data-variant-id', 'data-product-id', 'class'];
    
    attributesToCopy.forEach(attr => {
      if (source.hasAttribute(attr)) {
        target.setAttribute(attr, source.getAttribute(attr));
      }
    });
  }

  /**
   * Groups updates by parent element
   * @param {Array} updates - Updates to group
   * @returns {Map}
   */
  groupUpdatesByParent(updates) {
    const groups = new Map();
    
    updates.forEach(update => {
      const parent = update.element.parentElement;
      if (!groups.has(parent)) {
        groups.set(parent, []);
      }
      groups.get(parent).push(update);
    });
    
    return groups;
  }

  /**
   * Updates a group of elements under the same parent
   * @param {HTMLElement} parent - Parent element
   * @param {Array} updates - Updates to perform
   * @returns {Promise<void>}
   */
  async updateElementGroup(parent, updates) {
    // Batch DOM updates to minimize reflow
    const fragment = document.createDocumentFragment();
    
    for (const update of updates) {
      await this.updateElement(update.element, update.content, update.options);
    }
  }

  /**
   * Checks if browser supports animations
   * @returns {boolean}
   */
  supportsAnimations() {
    return typeof requestAnimationFrame !== 'undefined' && 
           !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Adds observer for element updates
   * @param {HTMLElement} element - Element to observe
   * @param {Function} callback - Callback function
   */
  observe(element, callback) {
    if (!this.observers.has(element)) {
      this.observers.set(element, new Set());
    }
    this.observers.get(element).add(callback);
  }

  /**
   * Removes observer for element updates
   * @param {HTMLElement} element - Element to stop observing
   * @param {Function} callback - Callback function
   */
  unobserve(element, callback) {
    if (this.observers.has(element)) {
      this.observers.get(element).delete(callback);
    }
  }

  /**
   * Notifies observers of element changes
   * @param {HTMLElement} element - Element that changed
   * @param {string} event - Event type
   */
  notifyObservers(element, event) {
    if (this.observers.has(element)) {
      this.observers.get(element).forEach(callback => {
        try {
          callback(element, event);
        } catch (error) {
          Debug.log('error', 'SmartUpdater: Observer callback failed', error);
        }
      });
    }
  }
}

// Export singleton instance
export const smartUpdater = new SmartUpdater(); 